import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { getStagesForCaseType, FILE_STATUSES, canSeeFinance } from '../../lib/constants';
import { useAuth } from '../../components/LoginGate';
import FollowupsPanel from '../../components/FollowupsPanel';

// Financial fields are optional here — the server strips them for staff.
interface CaseRow {
  id: number;
  file_reference: string | null;
  client_name: string;
  case_type: string | null;
  file_status: string;
  team_name: string | null;
  total_case_value?: number;
  amount_collected?: number;
}

interface Stage {
  id: number;
  stage_order: number;
  stage_name: string;
  completion_date: string | null;
  notes: string | null;
}

interface CaseDetail extends CaseRow {
  stages: Stage[];
  file_close_date?: string | null;
}

// Defensive — tolerate undefined so a staff user (who sees stripped
// finance fields) doesn't crash on `undefined.toLocaleString()`.
function fmt(v: number | null | undefined) {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StageTracker({ stages, caseType, onStageClick }: { stages: Stage[]; caseType: string | null; onStageClick: (s: Stage) => void }) {
  const names = getStagesForCaseType(caseType);
  const activeIdx = stages.reduce((acc, s, i) => (s.completion_date ? i : acc), -1);

  return (
    <div className="stage-tracker">
      {names.map((name, i) => {
        const stage = stages[i];
        const isCompleted = !!stage?.completion_date;
        const isActive = !isCompleted && i === activeIdx + 1;
        return (
          <div
            key={i}
            className={`stage-step${isCompleted ? ' completed' : isActive ? ' active' : ''}`}
            title={stage?.completion_date ? `Completed: ${stage.completion_date}` : 'Click to update'}
            onClick={() => stage && onStageClick(stage)}
          >
            <div className="stage-dot">
              {isCompleted ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
              ) : i + 1}
            </div>
            <div className="stage-label">{name}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientTrackerPage() {
  const { user } = useAuth();
  const showFinance = canSeeFinance(user?.role);
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [stageDate, setStageDate] = useState('');
  const [stageNotes, setStageNotes] = useState('');
  const [savingStage, setSavingStage] = useState(false);
  const [statusEdit, setStatusEdit] = useState<{ caseId: number; current: string } | null>(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch('/api/cases');
    if (res.ok) setCases(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  async function loadDetail(caseId: number) {
    if (expanded === caseId) { setExpanded(null); setDetail(null); return; }
    setExpanded(caseId);
    setDetailLoading(true);
    const [cr, sr] = await Promise.all([apiFetch(`/api/cases/${caseId}`), apiFetch(`/api/cases/${caseId}/stages`)]);
    const caseData = cr.ok ? await cr.json() : null;
    const stages = sr.ok ? await sr.json() : [];
    if (caseData) setDetail({ ...caseData, stages });
    setDetailLoading(false);
  }

  async function saveStage() {
    if (!editingStage) return;
    setSavingStage(true);
    await apiFetch(`/api/stages/${editingStage.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completion_date: stageDate || null, notes: stageNotes || null }),
    });
    setSavingStage(false);
    setEditingStage(null);
    if (expanded) loadDetail(expanded);
  }

  async function updateStatus(caseId: number, newStatus: string) {
    await apiFetch(`/api/cases/${caseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_status: newStatus }),
    });
    setStatusEdit(null);
    setCases(c => c.map(x => x.id === caseId ? { ...x, file_status: newStatus } : x));
    if (expanded === caseId && detail) setDetail({ ...detail, file_status: newStatus });
  }

  function openStageEdit(s: Stage) {
    setEditingStage(s);
    setStageDate(s.completion_date || '');
    setStageNotes(s.notes || '');
  }

  const statusColors: Record<string, string> = {
    Open: 'badge-blue', Closed: 'badge-gray', 'In-progress': 'badge-yellow',
    'In Court': 'badge-purple', 'Waiting on Client': 'badge-yellow',
    Appealed: 'badge-purple', Dropped: 'badge-red', Settled: 'badge-green',
  };

  const filtered = cases.filter(c => {
    const matchSearch = !search || c.client_name?.toLowerCase().includes(search.toLowerCase()) || c.file_reference?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || c.file_status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Client Tracker</h1>
          <p className="page-subtitle">Track case stages and client progress</p>
        </div>
      </div>

      <div className="filters-bar">
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Search client or reference…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-input" style={{ maxWidth: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {FILE_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        {(search || statusFilter) && <button className="btn" onClick={() => { setSearch(''); setStatusFilter(''); }}>Clear</button>}
      </div>

      {loading ? <div className="empty-state">Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 && <div className="empty-state">No cases found</div>}
          {filtered.map(c => (
            <div key={c.id} className="card" style={{ padding: 0 }}>
              <div
                style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
                onClick={() => loadDetail(c.id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{c.client_name}</span>
                    {c.file_reference && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.file_reference}</span>}
                    {c.case_type && <span className="badge badge-blue">{c.case_type}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                    {c.team_name && <span>{c.team_name}</span>}
                    {showFinance && <span>{fmt(c.total_case_value)} total</span>}
                    {showFinance && <span style={{ color: 'var(--success)' }}>{fmt(c.amount_collected)} collected</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span
                    className={`badge ${statusColors[c.file_status] || 'badge-gray'}`}
                    onClick={e => { e.stopPropagation(); setStatusEdit({ caseId: c.id, current: c.file_status }); }}
                    style={{ cursor: 'pointer' }}
                    title="Click to change status"
                  >
                    {c.file_status}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" style={{ transform: expanded === c.id ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              {expanded === c.id && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '14px 18px' }}>
                  {detailLoading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div> : detail && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>Case Stages</div>
                      <StageTracker stages={detail.stages} caseType={detail.case_type} onStageClick={openStageEdit} />
                      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>Click a stage to mark it as completed or add notes.</div>
                      <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                        <FollowupsPanel
                          caseId={detail.id}
                          caseClosed={!!detail.file_close_date || ['Closed', 'Dropped', 'Settled'].includes(detail.file_status)}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stage Edit Modal */}
      {editingStage && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditingStage(null)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Update Stage: {editingStage.stage_name}</h2>
              <button className="btn-icon" onClick={() => setEditingStage(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Completion Date (leave blank if not done)</label>
                <input className="form-input" type="date" value={stageDate} onChange={e => setStageDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={3} value={stageNotes} onChange={e => setStageNotes(e.target.value)} placeholder="Any notes for this stage…" />
              </div>
            </div>
            <div className="modal-footer">
              {stageDate && <button className="btn" style={{ marginRight: 'auto', color: 'var(--danger)' }} onClick={() => setStageDate('')}>Clear Date</button>}
              <button className="btn" onClick={() => setEditingStage(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveStage} disabled={savingStage}>{savingStage ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Status Edit Modal */}
      {statusEdit && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setStatusEdit(null)}>
          <div className="modal" style={{ maxWidth: 320 }}>
            <div className="modal-header">
              <h2>Change Status</h2>
              <button className="btn-icon" onClick={() => setStatusEdit(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Status</label>
                <select className="form-input" defaultValue={statusEdit.current} onChange={e => updateStatus(statusEdit.caseId, e.target.value)}>
                  {FILE_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
